// services/ConversationService.js
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  writeBatch,
  increment
} from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'
import { app } from '../firebase'
import UserService from './UserService'

class ConversationService {
  constructor () {
    this.db = getFirestore(app)
    this.localCache = new Map()
    this.maxCacheSize = 50
    this.maxMessagesPerConversation = 100
  }

  // CONVERSATION CRUD OPERATIONS

  /**
   * Neue Conversation erstellen
   */
  async createConversation (userId, deviceName, initialMessage = null) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const conversationId = this.generateConversationId()
      const now = new Date()

      const conversationData = {
        id: conversationId,
        userId: userId,
        deviceName: deviceName.trim(),
        title: this.generateConversationTitle(deviceName, initialMessage),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
        messageCount: 0,
        isArchived: false,
        tags: this.extractTags(initialMessage),
        metadata: {
          deviceType: this.detectDeviceType(deviceName),
          priority: initialMessage
            ? this.detectPriority(initialMessage)
            : 'normal',
          hasErrorCodes: initialMessage
            ? this.hasErrorCodes(initialMessage)
            : false
        }
      }

      // Save to company-scoped path
      await setDoc(
        doc(this.db, 'companies', companyId, 'conversations', conversationId),
        conversationData
      )

      // In lokalen Cache
      this.localCache.set(conversationId, {
        ...conversationData,
        messages: []
      })

      return conversationId
    } catch (error) {
      console.error('Fehler beim Erstellen der Conversation:', error)
      throw error
    }
  }

  /**
   * Message zu Conversation hinzufügen
   * NOTE: This method requires userId to get companyId
   */
  async addMessage (conversationId, message, userId, sources = [], performance = null) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const cleanPerformance = performance
        ? Object.fromEntries(
            Object.entries(performance).filter(([_, v]) => v !== undefined)
          )
        : null

      const messageData = {
        id: this.generateMessageId(),
        type: message.type, // 'user', 'bot', 'system'
        content: message.content,
        timestamp: serverTimestamp(),
        sources: sources || [],
        performance: performance || null,
        metadata: {
          queryAnalysis: message.queryAnalysis || null,
          isError: message.isError || false,
          tokens: message.content ? message.content.length : 0
        }
      }

      // Conversation-Dokument updaten (company-scoped path)
      const conversationRef = doc(
        this.db,
        'companies',
        companyId,
        'conversations',
        conversationId
      )
      await updateDoc(conversationRef, {
        updatedAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
        messageCount: increment(1),
        [`lastMessage`]: {
          content: message.content.substring(0, 100),
          type: message.type,
          timestamp: serverTimestamp()
        }
      })

      // Message als Sub-Dokument speichern (company-scoped path)
      const messageRef = doc(
        this.db,
        'companies',
        companyId,
        'conversations',
        conversationId,
        'messages',
        messageData.id
      )
      await setDoc(messageRef, messageData)

      // Lokalen Cache updaten
      this.updateLocalCache(conversationId, messageData)

      // Cache-Größe begrenzen
      this.limitCacheSize()

      return messageData.id
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Message:', error)
      throw error
    }
  }

  async loadConversation (conversationId, userId) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      // Erst im Cache schauen
      if (this.localCache.has(conversationId)) {
        const cached = this.localCache.get(conversationId)
        if (cached.messages && cached.messages.length > 0) {
          return cached
        }
      }

      // Conversation-Basis-Daten laden (company-scoped path)
      const conversationDoc = await getDoc(
        doc(this.db, 'companies', companyId, 'conversations', conversationId)
      )

      if (!conversationDoc.exists()) {
        throw new Error('Conversation nicht gefunden')
      }

      const conversationData = conversationDoc.data()

      // Sicherheitscheck: Nutzer berechtigt?
      if (conversationData.userId !== userId) {
        throw new Error('Keine Berechtigung für diese Conversation')
      }

      // Messages laden (letzte 50) (company-scoped path)
      const messagesQuery = query(
        collection(
          this.db,
          'companies',
          companyId,
          'conversations',
          conversationId,
          'messages'
        ),
        orderBy('timestamp', 'desc'),
        limit(50)
      )

      const messagesSnapshot = await getDocs(messagesQuery)

      // TIMESTAMP-FIX: Firestore Timestamps in Date-Objekte konvertieren
      const messages = messagesSnapshot.docs
        .map(doc => {
          const messageData = { id: doc.id, ...doc.data() }

          // Timestamp konvertieren
          if (
            messageData.timestamp &&
            typeof messageData.timestamp.toDate === 'function'
          ) {
            messageData.timestamp = messageData.timestamp.toDate()
          }

          return messageData
        })
        .reverse() // Chronologische Reihenfolge

      const fullConversation = {
        ...conversationData,
        // TIMESTAMP-FIX für Conversation-Daten
        createdAt:
          conversationData.createdAt?.toDate?.() || conversationData.createdAt,
        updatedAt:
          conversationData.updatedAt?.toDate?.() || conversationData.updatedAt,
        lastActivity:
          conversationData.lastActivity?.toDate?.() ||
          conversationData.lastActivity,
        messages
      }

      // In Cache speichern
      this.localCache.set(conversationId, fullConversation)

      return fullConversation
    } catch (error) {
      console.error('Fehler beim Laden der Conversation:', error)
      throw error
    }
  }

  /**
   * Alle Conversations eines Users laden
   */
  async getUserConversations (userId, limitCount = 20) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      // Query company-scoped conversations
      const conversationsQuery = query(
        collection(this.db, 'companies', companyId, 'conversations'),
        where('userId', '==', userId),
        where('isArchived', '==', false),
        orderBy('lastActivity', 'desc'),
        limit(limitCount)
      )

      const snapshot = await getDocs(conversationsQuery)

      // TIMESTAMP-FIX: Alle Timestamps konvertieren
      return snapshot.docs.map(doc => {
        const data = { id: doc.id, ...doc.data() }

        // Timestamps konvertieren
        return {
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
          lastActivity: data.lastActivity?.toDate?.() || data.lastActivity,
          // lastMessage Timestamp auch konvertieren
          lastMessage: data.lastMessage
            ? {
                ...data.lastMessage,
                timestamp:
                  data.lastMessage.timestamp?.toDate?.() ||
                  data.lastMessage.timestamp
              }
            : null
        }
      })
    } catch (error) {
      console.error('Fehler beim Laden der User-Conversations:', error)
      throw error
    }
  }
  /**
   * Conversation archivieren
   */
  async archiveConversation (conversationId, userId) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const conversationRef = doc(
        this.db,
        'companies',
        companyId,
        'conversations',
        conversationId
      )
      const conversationDoc = await getDoc(conversationRef)

      if (
        !conversationDoc.exists() ||
        conversationDoc.data().userId !== userId
      ) {
        throw new Error('Conversation nicht gefunden oder keine Berechtigung')
      }

      await updateDoc(conversationRef, {
        isArchived: true,
        archivedAt: serverTimestamp()
      })

      // Aus Cache entfernen
      this.localCache.delete(conversationId)
    } catch (error) {
      console.error('Fehler beim Archivieren der Conversation:', error)
      throw error
    }
  }

  /**
   * Conversation löschen (komplett)
   */
  async deleteConversation (conversationId, userId) {
    try {
      console.log(
        '🗑️ Lösche Conversation:',
        conversationId,
        'für User:',
        userId
      )

      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const conversationRef = doc(
        this.db,
        'companies',
        companyId,
        'conversations',
        conversationId
      )

      // Erst das Dokument abrufen
      const conversationDoc = await getDoc(conversationRef)

      if (!conversationDoc.exists()) {
        throw new Error('Conversation nicht gefunden')
      }

      const conversationData = conversationDoc.data()

      // Lokale Berechtigungsprüfung
      if (conversationData.userId !== userId) {
        throw new Error('Keine Berechtigung für diese Conversation')
      }

      // WICHTIG: Zuerst alle Messages löschen (Subcollection)
      const messagesQuery = query(
        collection(
          this.db,
          'companies',
          companyId,
          'conversations',
          conversationId,
          'messages'
        )
      )
      const messagesSnapshot = await getDocs(messagesQuery)

      // Batch Delete für Messages
      if (messagesSnapshot.size > 0) {
        const batch = writeBatch(this.db)
        messagesSnapshot.docs.forEach(msgDoc => {
          batch.delete(msgDoc.ref)
        })
        await batch.commit()
        console.log('✅ Messages gelöscht')
      }

      // Dann die Conversation selbst löschen
      await deleteDoc(conversationRef)

      // Aus Cache entfernen
      this.localCache.delete(conversationId)

      return true
    } catch (error) {
      console.error('❌ Fehler beim Löschen der Conversation:', error)
      console.error('Error Code:', error.code)
      console.error('Error Message:', error.message)

      // Detaillierte Fehlermeldung
      if (error.code === 'permission-denied') {
        throw new Error(
          'Keine Berechtigung: Bitte überprüfe, ob du angemeldet bist und die Conversation dir gehört.'
        )
      }

      throw error
    }
  }

  /**
   * Conversation-Titel updaten
   */
  async updateConversationTitle (conversationId, userId, newTitle) {
    try {
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(userId)

      const conversationRef = doc(
        this.db,
        'companies',
        companyId,
        'conversations',
        conversationId
      )
      const conversationDoc = await getDoc(conversationRef)

      if (
        !conversationDoc.exists() ||
        conversationDoc.data().userId !== userId
      ) {
        throw new Error('Conversation nicht gefunden oder keine Berechtigung')
      }

      await updateDoc(conversationRef, {
        title: newTitle,
        updatedAt: serverTimestamp()
      })

      // Cache updaten
      if (this.localCache.has(conversationId)) {
        const cached = this.localCache.get(conversationId)
        cached.title = newTitle
        this.localCache.set(conversationId, cached)
      }
    } catch (error) {
      console.error('Fehler beim Updaten des Titels:', error)
      throw error
    }
  }

  // HELPER METHODS

  generateConversationId () {
    return Date.now().toString(36) + Math.random().toString(36).substr(2)
  }

  generateMessageId () {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
  }

  generateConversationTitle (deviceName, initialMessage) {
    if (!initialMessage) return `${deviceName} - Neue Unterhaltung`

    const errorCodeMatch = initialMessage.match(/\b([EF]-?\d{1,5})\b/i)
    if (errorCodeMatch) {
      return `${deviceName} - ${errorCodeMatch[1]}`
    }

    const shortMessage = initialMessage.substring(0, 30)
    return `${deviceName} - ${shortMessage}${
      initialMessage.length > 30 ? '...' : ''
    }`
  }

  extractTags (message) {
    if (!message) return []

    const tags = []
    const text = message.toLowerCase()

    if (/\b(fehler|error|problem)\b/.test(text)) tags.push('error')
    if (/\b(wartung|maintenance|service)\b/.test(text)) tags.push('maintenance')
    if (/\b(ersatzteil|spare.*part)\b/.test(text)) tags.push('parts')
    if (/\b(kalibrierung|calibration)\b/.test(text)) tags.push('calibration')
    if (/\b([ef]-?\d{1,5})\b/.test(text)) tags.push('error-code')

    return tags
  }

  detectDeviceType (deviceName) {
    const name = deviceName.toLowerCase()
    if (/analyzer|analyse/.test(name)) return 'analyzer'
    if (/microscop/.test(name)) return 'microscope'
    if (/spectro/.test(name)) return 'spectrometer'
    if (/centrifuge/.test(name)) return 'centrifuge'
    return 'unknown'
  }

  detectPriority (message) {
    const urgent = /\b(urgent|dringend|critical|notfall|emergency|sofort)\b/i
    const high = /\b(wichtig|important|problem|fehler|error|defekt)\b/i

    if (urgent.test(message)) return 'urgent'
    if (high.test(message)) return 'high'
    return 'normal'
  }

  hasErrorCodes (message) {
    return /\b([EF]-?\d{1,5}|error.*\d+|fehler.*\d+)\b/i.test(message)
  }

  updateLocalCache (conversationId, messageData) {
    if (this.localCache.has(conversationId)) {
      const conversation = this.localCache.get(conversationId)
      if (!conversation.messages) conversation.messages = []

      conversation.messages.push(messageData)

      // Messages begrenzen
      if (conversation.messages.length > this.maxMessagesPerConversation) {
        conversation.messages = conversation.messages.slice(
          -this.maxMessagesPerConversation
        )
      }

      this.localCache.set(conversationId, conversation)
    }
  }

  limitCacheSize () {
    if (this.localCache.size > this.maxCacheSize) {
      const entries = Array.from(this.localCache.entries())
      const toDelete = entries.slice(0, entries.length - this.maxCacheSize)
      toDelete.forEach(([id]) => this.localCache.delete(id))
    }
  }

  // Cache Management
  clearCache () {
    this.localCache.clear()
  }

  getCacheSize () {
    return this.localCache.size
  }
}

// Singleton Export
export default new ConversationService()
